"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { isLoggedIn } from "@/app/funcs/api";
import {
    getQuizQuestions,
    getQuizzesList,
    submitQuizAttempt,
    getOrCreateSessionId,
    formatAnswerForSubmission,
} from "@/app/services/quizzes/helpers/quizApi";
import { getQuizExpiryMs, formatRemainingTime } from "@/app/services/quizzes/helpers/quizExpiry";
import { normalizeQuizFlag } from "@/app/services/quizzes/helpers/quizBooleans";
import { getPassingThreshold, hasPassedQuiz } from "@/app/services/quizzes/helpers/quizPassing";
import { fetchSectionQuestions, submitSectionQuiz } from "../lib/courseSectionsApi";
import Image from "next/image";
import trophy from "@/app/assets/images/trophy.gif";
import OhNoGif from "@/app/assets/images/oh-no.gif";

/**
 * In-modal / in-page quiz take flow (no iframe). Mirrors core behavior of take/page.jsx.
 */
export default function CourseQuizFlow({
    quizId,
    onClose,
    onComplete,
    mode = "quiz",
    courseId,
    sectionKey,
    sectionThreshold,
}) {
    const isSection = mode === "section";
    const [authChecked, setAuthChecked] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [quizInfo, setQuizInfo] = useState(null);
    const [quizMeta, setQuizMeta] = useState(null);
    const [sessionId, setSessionId] = useState("");
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [selectedAnswers, setSelectedAnswers] = useState([]);
    const [paragraphAnswer, setParagraphAnswer] = useState("");
    const [answers, setAnswers] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [phase, setPhase] = useState("quiz"); // 'quiz' | 'results'
    const [resultPayload, setResultPayload] = useState(null);
    const [timeRemaining, setTimeRemaining] = useState(1500);
    const [remainingMs, setRemainingMs] = useState(null);
    const [isExpired, setIsExpired] = useState(false);
    const currentAnswerRef = useRef({ index: -1, value: null });

    const wordpressQuizId = Number(quizId);
    const disableBecauseExpired = isExpired;
    const currentQuestion = questions[currentQuestionIndex];

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const ok = await isLoggedIn();
            if (cancelled) return;
            if (!ok) {
                toast.error("يجب تسجيل الدخول لبدء الاختبار.", { position: "top-center" });
                setError("يجب تسجيل الدخول");
                setLoading(false);
                return;
            }
            setAuthChecked(true);
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!authChecked || !wordpressQuizId) return;

        const fetchQuestions = async () => {
            let questionsLimit = 30;
            try {
                setLoading(true);
                setError(null);

                const session = getOrCreateSessionId();
                setSessionId(session);

                let data;

                if (isSection) {
                    // Gated section: fetch only the section's selected questions.
                    const res = await fetchSectionQuestions(courseId, sectionKey);
                    if (!res.ok) {
                        setError(
                            res.status === 403
                                ? res.message || "هذا القسم مقفل. أكمل القسم السابق أولاً."
                                : res.error || "تعذر تحميل أسئلة القسم."
                        );
                        setLoading(false);
                        return;
                    }
                    // The configured question numbers (q_nums) may not resolve to real
                    // content yet (quiz has no/fewer saved questions) — say so precisely
                    // instead of a generic "no questions" message, since this is a config
                    // gap admins need to notice and fix, not a transient error.
                    if (!Array.isArray(res.questions) || res.questions.length === 0) {
                        const nums = Array.isArray(res.questionNumbers) ? res.questionNumbers : [];
                        setError(
                            nums.length > 0
                                ? `الأسئلة رقم (${nums.join('، ')}) غير موجودة في اختبار #${res.quizId ?? ''} — لم يتم حفظ أسئلة كافية في هذا الاختبار بعد.`
                                : "لا توجد أسئلة متاحة لهذا الاختبار."
                        );
                        setLoading(false);
                        return;
                    }
                    data = {
                        title: "اختبار القسم",
                        quizId: wordpressQuizId,
                        questions: Array.isArray(res.questions) ? res.questions : [],
                    };
                } else {
                    try {
                        const apiResponse = await getQuizzesList({ per_page: 80, page: 1 });
                        const apiQuizzes = apiResponse?.items || (Array.isArray(apiResponse) ? apiResponse : []);
                        const foundQuiz = apiQuizzes.find((q) => q.id === wordpressQuizId);
                        if (foundQuiz) {
                            const parsedQuestionCount = parseInt(foundQuiz.q_count);
                            if (!isNaN(parsedQuestionCount) && parsedQuestionCount > 0) {
                                questionsLimit = parsedQuestionCount;
                            }
                            const durationMinutes = foundQuiz.q_duration ? parseInt(foundQuiz.q_duration) : 25;
                            setTimeRemaining(
                                !isNaN(durationMinutes) && durationMinutes > 0 ? durationMinutes * 60 : 1500
                            );
                            setQuizMeta({
                                createdAt: foundQuiz.createdAt,
                                expirationAt: foundQuiz.expirationAt ?? null,
                                ramadan: Boolean(foundQuiz.ramadan),
                            });
                        }
                    } catch (metaErr) {
                        console.warn(metaErr);
                    }

                    data = await getQuizQuestions(wordpressQuizId, questionsLimit);
                }

                if (data.title) {
                    setQuizInfo({ title: data.title, quizId: data.quizId || wordpressQuizId });
                }

                if (data.questions && data.questions.length > 0) {
                    const withShuffled = data.questions.map((q) => {
                        if (q.options && Array.isArray(q.options) && q.options.length > 1) {
                            const shuffled = [...q.options];
                            for (let i = shuffled.length - 1; i > 0; i--) {
                                const j = Math.floor(Math.random() * (i + 1));
                                [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
                            }
                            return { ...q, displayOptions: shuffled };
                        }
                        return { ...q, displayOptions: q.options || [] };
                    });
                    setQuestions(withShuffled);
                } else {
                    setError("لا توجد أسئلة متاحة لهذا الاختبار.");
                }
            } catch (err) {
                setError(err.message || "فشل تحميل الأسئلة");
            } finally {
                setLoading(false);
            }
        };

        fetchQuestions();
    }, [authChecked, wordpressQuizId, isSection, courseId, sectionKey]);

    useEffect(() => {
        const expireMs = getQuizExpiryMs(quizMeta);
        if (expireMs == null) {
            setRemainingMs(null);
            setIsExpired(false);
            return;
        }
        const tick = () => {
            const diff = expireMs - Date.now();
            if (diff <= 0) {
                setRemainingMs(0);
                setIsExpired(true);
            } else {
                setRemainingMs(diff);
                setIsExpired(false);
            }
        };
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [quizMeta]);

    useEffect(() => {
        if (disableBecauseExpired || phase !== "quiz") return;
        const id = setInterval(() => {
            setTimeRemaining((prev) => Math.max(0, prev - 1));
        }, 1000);
        return () => clearInterval(id);
    }, [disableBecauseExpired, phase]);

    useEffect(() => {
        if (questions.length === 0 || currentQuestionIndex >= questions.length) return;
        const cq = questions[currentQuestionIndex];
        const savedAnswer = answers[currentQuestionIndex];
        if (cq.type === "multiple_choice") {
            setSelectedAnswers(Array.isArray(savedAnswer) ? savedAnswer : []);
            setSelectedAnswer(null);
            setParagraphAnswer("");
        } else if (cq.type === "paragraph") {
            setParagraphAnswer(typeof savedAnswer === "string" ? savedAnswer : "");
            setSelectedAnswer(null);
            setSelectedAnswers([]);
        } else {
            setSelectedAnswer(savedAnswer ?? null);
            setSelectedAnswers([]);
            setParagraphAnswer("");
        }
    }, [currentQuestionIndex, questions, answers]);

    useEffect(() => {
        if (questions.length === 0 || currentQuestionIndex >= questions.length) return;
        const cq = questions[currentQuestionIndex];
        let value = null;
        if (cq.type === "multiple_choice") {
            value = selectedAnswers.length > 0 ? selectedAnswers : null;
        } else if (cq.type === "paragraph") {
            value = paragraphAnswer.trim() || null;
        } else {
            value = selectedAnswer;
        }
        currentAnswerRef.current = { index: currentQuestionIndex, value };
    }, [questions, currentQuestionIndex, selectedAnswer, selectedAnswers, paragraphAnswer]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    };

    const hasAnswer = useCallback(() => {
        if (!currentQuestion) return false;
        if (currentQuestion.type === "multiple_choice") {
            return selectedAnswers.length > 0;
        }
        if (currentQuestion.type === "paragraph") {
            return paragraphAnswer.trim().length > 0;
        }
        return selectedAnswer !== null;
    }, [currentQuestion, selectedAnswers, paragraphAnswer, selectedAnswer]);

    const handleSubmitQuiz = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);

        const formattedAnswers = [];
        const { index: liveIndex, value: liveValue } = currentAnswerRef.current || {};

        questions.forEach((question, index) => {
            const answerData = liveIndex === index ? liveValue : answers[index];
            if (
                answerData !== null &&
                answerData !== undefined &&
                answerData !== "" &&
                !(Array.isArray(answerData) && answerData.length === 0)
            ) {
                try {
                    const formatted = formatAnswerForSubmission(question, answerData);
                    if (formatted.uid && formatted.type) formattedAnswers.push(formatted);
                } catch (e) {
                    console.warn(e);
                }
            }
        });

        if (formattedAnswers.length === 0) {
            toast.warning("يرجى الإجابة على سؤال واحد على الأقل", { position: "top-center" });
            setIsSubmitting(false);
            return;
        }

        try {
            if (isSection) {
                const res = await submitSectionQuiz(courseId, sectionKey, formattedAnswers);
                if (!res.ok) {
                    toast.error(res.message || res.error || "فشل الإرسال", { position: "top-center" });
                    return;
                }
                const pct = Math.round(res.percentage || 0);
                const passed = Boolean(res.passed);
                const resultsWithTitle = {
                    sectionMode: true,
                    quizTitle: quizInfo?.title || "اختبار القسم",
                    percentageObjective: pct,
                    percentage: pct,
                    passed,
                    passingThreshold:
                        typeof res.threshold === "number" ? res.threshold : sectionThreshold ?? 80,
                    nextSectionUnlocked: Boolean(res.nextSectionUnlocked),
                    course: res.course || null,
                };
                setResultPayload(resultsWithTitle);
                onComplete?.({ passed, percentageObjective: pct, result: resultsWithTitle, course: res.course || null });
                toast.success("تم إرسال إجاباتك!", { position: "top-center", autoClose: 2000 });
                setPhase("results");
                return;
            }

            const servedQuestionUids = questions
                .map((q) => (q?.uid != null ? String(q.uid).trim() : ""))
                .filter((uid) => uid !== "");
            const result = await submitQuizAttempt(
                wordpressQuizId,
                sessionId,
                formattedAnswers,
                servedQuestionUids
            );
            const resultsWithTitle = {
                ...result,
                quizTitle: quizInfo?.title || "الاختبار",
                quizRamadan: Boolean(quizMeta?.ramadan),
            };
            setResultPayload(resultsWithTitle);
            sessionStorage.setItem(`quiz_result_${result.attemptId}`, JSON.stringify(resultsWithTitle));

            const pct = Math.round(resultsWithTitle.percentageObjective || 0);
            const ramadan = normalizeQuizFlag(resultsWithTitle?.quizRamadan) || normalizeQuizFlag(resultsWithTitle?.ramadan);
            const passed = hasPassedQuiz(pct, ramadan);

            onComplete?.({
                passed,
                percentageObjective: pct,
                result: resultsWithTitle,
            });

            toast.success("تم إرسال إجاباتك!", { position: "top-center", autoClose: 2000 });
            setPhase("results");
        } catch (err) {
            toast.error(err.message || "فشل الإرسال", { position: "top-center" });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleNext = () => {
        if (questions.length === 0 || !currentQuestion) return;
        let answerValue = null;
        if (currentQuestion.type === "multiple_choice") {
            answerValue = selectedAnswers.length > 0 ? selectedAnswers : null;
        } else if (currentQuestion.type === "paragraph") {
            answerValue = paragraphAnswer.trim() || null;
        } else {
            answerValue = selectedAnswer;
        }
        setAnswers((prev) => ({ ...prev, [currentQuestionIndex]: answerValue }));

        if (currentQuestionIndex < questions.length - 1) {
            setCurrentQuestionIndex((i) => i + 1);
        } else {
            void handleSubmitQuiz();
        }
    };

    const handlePrevious = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex((i) => i - 1);
        }
    };

    const handleAnswerSelect = (optionKey) => {
        if (isSubmitting || disableBecauseExpired || !currentQuestion) return;
        if (currentQuestion.type === "multiple_choice") {
            setSelectedAnswers((prev) =>
                prev.includes(optionKey) ? prev.filter((k) => k !== optionKey) : [...prev, optionKey]
            );
        } else {
            setSelectedAnswer(optionKey);
        }
    };

    const handleTrueFalseSelect = (v) => {
        if (!isSubmitting && !disableBecauseExpired) setSelectedAnswer(v);
    };

    if (!authChecked && loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" />
                <p className="mt-2 text-muted small">جاري التحقق...</p>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" />
                <p className="mt-2 text-muted">جاري تحميل الأسئلة...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center py-4 px-3">
                <p className="text-danger">{error}</p>
                <button type="button" className="btn btn-outline-secondary btn-sm mt-2" onClick={onClose}>
                    إغلاق
                </button>
            </div>
        );
    }

    if (phase === "results" && resultPayload) {
        const percentage = Math.round(resultPayload.percentageObjective || 0);
        const isRamadanQuiz =
            normalizeQuizFlag(resultPayload?.quizRamadan) || normalizeQuizFlag(resultPayload?.ramadan);
        const passingThreshold = resultPayload.sectionMode
            ? Number(resultPayload.passingThreshold ?? 80)
            : getPassingThreshold(isRamadanQuiz);
        const passed = resultPayload.sectionMode
            ? Boolean(resultPayload.passed)
            : hasPassedQuiz(percentage, isRamadanQuiz);
        const courseState = resultPayload.sectionMode ? resultPayload.course : null;

        return (
            <div className="text-center py-3 px-2" dir="rtl">
                <div className="card border-0 shadow-sm p-4 mx-auto" style={{ maxWidth: 420 }}>
                    <div className="mb-3">
                        <Image
                            src={passed ? trophy : OhNoGif}
                            alt=""
                            width={passed ? 120 : 140}
                            height={passed ? 120 : 140}
                            className="mx-auto d-block"
                            unoptimized
                        />
                    </div>
                    <h5 className="fw-bold mb-2">{resultPayload.quizTitle}</h5>
                    <p className="display-6 fw-bold mb-1">{percentage}%</p>
                    <p className="text-muted small mb-3">
                        عتبة النجاح: {passingThreshold}% {isRamadanQuiz ? "(رمضان)" : ""}
                    </p>
                    {passed ? (
                        <span className="badge bg-success fs-6 px-3 py-2">ناجح</span>
                    ) : (
                        <span className="badge bg-danger fs-6 px-3 py-2">لم تنجح</span>
                    )}
                    {courseState ? (
                        <div className="mt-3 small text-muted">
                            درجتك في الدورة حتى الآن: {Math.round(courseState.courseScore)} / {Math.round(courseState.configuredMaxScore)} ({Math.round(courseState.coursePercentage)}%)
                            {courseState.courseCompleted ? (
                                <div className="text-success fw-bold mt-1">🎉 أكملت الدورة!</div>
                            ) : null}
                        </div>
                    ) : null}
                    <div className="d-flex flex-column gap-2 mt-4">
                        <button type="button" className="btn btn-primary rounded-pill" onClick={onClose}>
                            متابعة الدورة
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!currentQuestion || questions.length === 0) {
        return (
            <div className="text-center py-4 text-muted">لا توجد أسئلة</div>
        );
    }

    const opts = currentQuestion.displayOptions ?? currentQuestion.options ?? [];

    return (
        <div className="d-flex flex-column h-100" style={{ minHeight: 280, maxHeight: "min(70vh, 640px)" }}>
            <div className="px-2 py-2 border-bottom bg-light d-flex justify-content-between align-items-center flex-wrap gap-2 small">
                <span>
                    السؤال {currentQuestionIndex + 1} من {questions.length}
                </span>
                <div className="d-flex align-items-center gap-2">
                    {remainingMs !== null && (
                        <span className="badge bg-secondary">
                            {isExpired ? "انتهى العرض" : formatRemainingTime(remainingMs)}
                        </span>
                    )}
                    <span className="badge bg-dark">
                        <i className="bi bi-clock me-1" />
                        {formatTime(timeRemaining)}
                    </span>
                </div>
            </div>

            <div className="flex-grow-1 overflow-auto px-3 py-3">
                <div
                    className="fw-bold mb-4 fs-5"
                    dangerouslySetInnerHTML={{ __html: currentQuestion.prompt }}
                />
                {currentQuestion.type === "multiple_choice" && (
                    <span className="text-danger small">( اختر جميع الإجابات الصحيحة )</span>
                )}

                {currentQuestion.imageUrl && (
                    <div className="mb-3 text-center">
                        <img
                            src={currentQuestion.imageUrl}
                            alt=""
                            className="img-fluid rounded"
                            style={{ maxHeight: 280 }}
                        />
                    </div>
                )}

                {currentQuestion.type === "paragraph" ? (
                    <textarea
                        value={paragraphAnswer}
                        onChange={(e) => setParagraphAnswer(e.target.value)}
                        disabled={isSubmitting || disableBecauseExpired}
                        className="form-control"
                        rows={6}
                        placeholder="اكتب إجابتك هنا..."
                    />
                ) : currentQuestion.type === "true_false" ? (
                    <div className="d-flex flex-column gap-2">
                        <button
                            type="button"
                            onClick={() => handleTrueFalseSelect(true)}
                            disabled={isSubmitting || disableBecauseExpired}
                            className={`btn p-3 rounded-3 text-start ${selectedAnswer === true ? "btn-primary" : "btn-outline-secondary"}`}
                        >
                            صحيح
                        </button>
                        <button
                            type="button"
                            onClick={() => handleTrueFalseSelect(false)}
                            disabled={isSubmitting || disableBecauseExpired}
                            className={`btn p-3 rounded-3 text-start ${selectedAnswer === false ? "btn-primary" : "btn-outline-secondary"}`}
                        >
                            خطأ
                        </button>
                    </div>
                ) : (
                    <div className="d-flex flex-column gap-2">
                        {opts.map((option, index) => {
                            const isSelected =
                                currentQuestion.type === "multiple_choice"
                                    ? selectedAnswers.includes(option.key)
                                    : selectedAnswer === option.key;
                            return (
                                <button
                                    type="button"
                                    key={option.key || index}
                                    onClick={() => handleAnswerSelect(option.key)}
                                    disabled={isSubmitting || disableBecauseExpired}
                                    className={`btn p-3 rounded-3 text-start d-flex justify-content-between align-items-center ${
                                        isSelected ? "btn-primary" : "btn-outline-secondary"
                                    }`}
                                >
                                    <span>{option.label}</span>
                                    {isSelected && <i className="bi bi-check-lg" />}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            <div className="border-top bg-white p-3 d-flex justify-content-between align-items-center gap-2 flex-shrink-0">
                <button
                    type="button"
                    className="btn btn-outline-secondary rounded-pill"
                    disabled={currentQuestionIndex === 0 || isSubmitting}
                    onClick={handlePrevious}
                >
                    <i className="bi bi-chevron-right me-1" />
                    السابق
                </button>
                <button
                    type="button"
                    className="btn btn-primary rounded-pill px-4"
                    disabled={!hasAnswer() || isSubmitting || disableBecauseExpired}
                    onClick={handleNext}
                >
                    {isSubmitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            جاري الإرسال...
                        </>
                    ) : currentQuestionIndex < questions.length - 1 ? (
                        <>
                            التالي
                            <i className="bi bi-chevron-left ms-1" />
                        </>
                    ) : currentQuestion?.type === "multiple_choice" ? (
                        "تسجيل الإجابات"
                    ) : (
                        "تسجيل الإجابة"
                    )}
                </button>
            </div>
        </div>
    );
}
